import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import PageHeader from "../components/PageHeader";
import WinnerRank from "../components/WinnerRank";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getActiveSeason, getMlbLeaderboard, getMlbPool, getMlbPlayers, getMlbPayouts, getMlbSmackMessages, postMlbSmackMessage, getMlbSmackReplies, postMlbSmackReply, reactMlbSmack, deleteMlbSmackMessage } from "../api/mlbHrDerby";
import RulesPayoutsPanel from "../components/RulesPayoutsPanel";
import SmackBoard from "../components/SmackBoard";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE    = "#00007f";

const MLB_RULES = [
  {
    heading: "Roster",
    lines: [
      "Pick six players you believe will hit the most home runs during the regular season.",
      "Your six players' combined previous-season HR total must be 130 or fewer.",
    ],
  },
  {
    heading: "Scoring",
    lines: [
      "Your score is the total home runs hit by your six active players during the regular season.",
      "Live scores update daily.",
    ],
  },
  {
    heading: "Swap",
    lines: [
      "One swap allowed during the season.",
      "Drop a player from your roster and add an eligible replacement.",
    ],
  },
];

const MLB_ABBREVS = {
  "Arizona Diamondbacks": "ARI", "Atlanta Braves": "ATL", "Baltimore Orioles": "BAL",
  "Boston Red Sox": "BOS", "Chicago Cubs": "CHC", "Chicago White Sox": "CWS",
  "Cincinnati Reds": "CIN", "Cleveland Guardians": "CLE", "Colorado Rockies": "COL",
  "Detroit Tigers": "DET", "Houston Astros": "HOU", "Kansas City Royals": "KC",
  "Los Angeles Angels": "LAA", "Los Angeles Dodgers": "LAD", "Miami Marlins": "MIA",
  "Milwaukee Brewers": "MIL", "Minnesota Twins": "MIN", "New York Mets": "NYM",
  "New York Yankees": "NYY", "Oakland Athletics": "OAK", "Athletics": "ATH",
  "Sacramento Athletics": "ATH", "Philadelphia Phillies": "PHI", "Pittsburgh Pirates": "PIT",
  "San Diego Padres": "SD", "San Francisco Giants": "SF", "Seattle Mariners": "SEA",
  "St. Louis Cardinals": "STL", "Tampa Bay Rays": "TB", "Texas Rangers": "TEX",
  "Toronto Blue Jays": "TOR", "Washington Nationals": "WSH",
};

function teamAbbr(name) {
  if (!name) return "—";
  if (MLB_ABBREVS[name]) return MLB_ABBREVS[name];
  if (name.length <= 4) return name;
  return name.substring(0, 3).toUpperCase();
}

const MLB_TEAM_IDS = {
  ARI: 109, ATL: 144, BAL: 110, BOS: 111, CHC: 112, CWS: 145,
  CIN: 113, CLE: 114, COL: 115, DET: 116, HOU: 117, KC:  118,
  LAA: 108, LAD: 119, MIA: 146, MIL: 158, MIN: 142, NYM: 121,
  NYY: 147, ATH: 133, PHI: 143, PIT: 134, SD:  135, SF:  137,
  SEA: 136, STL: 138, TB:  139, TEX: 140, TOR: 141, WSH: 120,
};

function teamLogoUrl(teamName) {
  const id = MLB_TEAM_IDS[teamAbbr(teamName)];
  return id ? `https://www.mlbstatic.com/team-logos/${id}.svg` : null;
}

function playerHeadshotUrl(mlbId) {
  if (!mlbId) return null;
  return `https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_68,q_auto:best/v1/people/${mlbId}/headshot/67/current`;
}

export default function MlbHrDerbyActiveHome() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const { poolId }       = useParams();
  const numericPoolId    = Number(poolId);

  const username    = user?.username || "Player";

  const [season,       setSeason]       = useState(null);
  const [leaderboard,  setLeaderboard]  = useState([]);
  const [poolFinished, setPoolFinished] = useState(false);
  const [allPlayers,   setAllPlayers]   = useState([]);
  const [poolName,     setPoolName]     = useState("");
  const [poolDetail,   setPoolDetail]   = useState(null);
  const [poolPayouts,  setPoolPayouts]  = useState([]);
  const [loadingPage,  setLoadingPage]  = useState(true);

  const [pageError,        setPageError]        = useState("");
  const [selectedTeam,     setSelectedTeam]     = useState(null);
  const [playerSearch,     setPlayerSearch]     = useState("");
  const [playerStatsPage,  setPlayerStatsPage]  = useState(1);

  const STATS_PAGE_SIZE = 50;

  const { isOpen: isTeamOpen, onOpen: onTeamOpen, onClose: onTeamClose } = useDisclosure();

  const rosterLocked = (() => {
    if (!season?.roster_lock_date) return false;
    const lock = new Date(season.roster_lock_date);
    lock.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today >= lock;
  })();

  // ── Data load ──────────────────────────────────────────────────────────────
  useEffect(() => {
    async function loadPage() {
      if (!numericPoolId || Number.isNaN(numericPoolId)) {
        setPageError("Invalid pool.");
        setLoadingPage(false);
        return;
      }
      try {
        setPageError("");
        const season = await getActiveSeason();
        setSeason(season);
        const [lbData, poolData, players, payouts] = await Promise.all([
          getMlbLeaderboard(season.id, numericPoolId),
          getMlbPool(season.id, numericPoolId),
          getMlbPlayers(season.id),
          getMlbPayouts(season.id, numericPoolId),
        ]);
        setLeaderboard(lbData.leaderboard || []);
        setPoolFinished(lbData.pool_finished === true);
        setPoolDetail(poolData);
        setPoolName(poolData.name || "");
        setAllPlayers(players);
        setPoolPayouts(payouts);
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Unable to load page.";
        setPageError(typeof msg === "string" ? msg : "Unable to load page.");
      } finally {
        setLoadingPage(false);
      }
    }
    loadPage();
  }, [numericPoolId]);

  // ── Derived data ───────────────────────────────────────────────────────────

  function isMyEntry(entry) {
    return (entry.username || "").trim().toLowerCase() === username.trim().toLowerCase();
  }

  const myPlayerIds = useMemo(() => {
    const ids = new Set();
    const lowerUser = username.trim().toLowerCase();
    for (const entry of leaderboard) {
      if ((entry.username || "").trim().toLowerCase() === lowerUser) {
        for (const p of entry.players || []) ids.add(p.player_id);
      }
    }
    return ids;
  }, [leaderboard, username]);

  const allPlayersRanked = useMemo(() => {
    const sorted = [...allPlayers]
      .sort((a, b) => (b.curr_season?.home_runs ?? 0) - (a.curr_season?.home_runs ?? 0));
    let rank = 1;
    return sorted.map((p, i) => {
      const curr_hrs = p.curr_season?.home_runs ?? 0;
      if (i > 0 && curr_hrs < (sorted[i - 1].curr_season?.home_runs ?? 0)) rank = i + 1;
      return {
        player_id:    p.id,
        name:         p.name,
        team:         p.team,
        position:     p.position,
        curr_hrs,
        prev_hrs:     p.prev_season?.home_runs ?? 0,
        games:        p.curr_season?.games ?? 0,
        at_bats:      p.curr_season?.at_bats ?? 0,
        batting_avg:  p.curr_season?.batting_avg ?? null,
        slugging_pct: p.curr_season?.slugging_pct ?? null,
        hr_rank:      rank,
      };
    });
  }, [allPlayers]);

  const filteredPlayersRanked = useMemo(() => {
    if (!playerSearch) return allPlayersRanked;
    const q = playerSearch.toLowerCase();
    return allPlayersRanked.filter((p) => p.name.toLowerCase().includes(q));
  }, [allPlayersRanked, playerSearch]);

  const pagedPlayersRanked = useMemo(() => {
    const start = (playerStatsPage - 1) * STATS_PAGE_SIZE;
    return filteredPlayersRanked.slice(start, start + STATS_PAGE_SIZE);
  }, [filteredPlayersRanked, playerStatsPage, STATS_PAGE_SIZE]);

  const statsTotalPages = Math.max(1, Math.ceil(filteredPlayersRanked.length / STATS_PAGE_SIZE));

  useEffect(() => setPlayerStatsPage(1), [playerSearch]);

  const uniquePicks = useMemo(() => {
    const map = {};
    for (const entry of leaderboard) {
      for (const p of entry.players || []) {
        if (!map[p.player_id]) map[p.player_id] = { ...p, team_names: [] };
        map[p.player_id].team_names.push(entry.team_name);
      }
    }
    return Object.values(map)
      .filter((p) => p.team_names.length === 1)
      .sort((a, b) => b.curr_hrs - a.curr_hrs);
  }, [leaderboard]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  function hrLeader(entry) {
    const active = (entry.players || []).filter((p) => p.is_active);
    if (!active.length) return null;
    return active.reduce((best, p) => (p.curr_hrs > best.curr_hrs ? p : best), active[0]);
  }

  function sinceYesterday(entry) {
    if (entry.hrs_yesterday == null) return { text: "—", color: "gray.400" };
    const delta = entry.total_hrs - entry.hrs_yesterday;
    if (delta > 0) return { text: `+${delta}`, color: "green.600" };
    if (delta < 0) return { text: `${delta}`, color: "red.600" };
    return { text: "0", color: "gray.500" };
  }

  function fmtStat(val, decimals = 3) {
    if (val == null) return "—";
    return Number(val).toFixed(decimals);
  }

  function openTeamModal(entry) {
    setSelectedTeam(entry);
    onTeamOpen();
  }

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loadingPage) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <Flex minH="100vh" align="center" justify="center" direction="column" gap={4}>
          <Spinner size="xl" thickness="4px" />
          <Text>Loading MLB HR Derby...</Text>
        </Flex>
      </Box>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">

      <PageHeader
        title="MLB HR DERBY"
        onBack={() => navigate("/mlb-hr-derby")}
        user={user}
        onLogout={handleLogout}
      />

      {/* ── Page Body ── */}
      <Container maxW="container.xl" py={8}>
        {pageError && (
          <Alert status="error" borderRadius="md" mb={6}>
            <AlertIcon />{pageError}
          </Alert>
        )}

        <SmackBoard
          poolId={numericPoolId}
          seasonId={season?.id}
          apiFns={{ getMessages: getMlbSmackMessages, postMessage: postMlbSmackMessage, getReplies: getMlbSmackReplies, postReply: postMlbSmackReply, react: reactMlbSmack, deleteMessage: deleteMlbSmackMessage }}
        />

        {/* Action buttons */}
        <HStack mb={6} spacing={4} flexWrap="wrap" align="center">
          {!rosterLocked && (
            <Button
              size="sm"
              bg="white"
              color={TOP_BAR_BLUE}
              onClick={() => navigate(`/mlb-hr-derby/current/${numericPoolId}/draft`)}
              _hover={{ bg: "gray.100" }}
            >
              Draft Team
            </Button>
          )}
          <Button
            size={{ base: "xs", md: "sm" }}
            bg="white"
            color={TOP_BAR_BLUE}
            onClick={() => navigate(`/mlb-hr-derby/current/${numericPoolId}/rosters`)}
            _hover={{ bg: "gray.100" }}
          >
            Full Rosters View
          </Button>
          <Button
            size={{ base: "xs", md: "sm" }}
            bg="red.600"
            color="white"
            onClick={() => navigate(`/mlb-hr-derby/current/${numericPoolId}/swap`)}
            _hover={{ bg: "red.700" }}
          >
            Manage Swap
          </Button>
          <Box flex="1" display={{ base: "none", md: "block" }} />
          {(poolDetail?.owner_user_id === user?.id || poolDetail?.current_user_is_co_admin) && (
            <Button
              size={{ base: "xs", md: "sm" }}
              variant="outline"
              color="white"
              borderColor="whiteAlpha.500"
              _hover={{ bg: "whiteAlpha.200" }}
              onClick={() => navigate(`/mlb-hr-derby/setup/${numericPoolId}`)}
            >
              Pool Settings
            </Button>
          )}
        </HStack>

        {/* ── Top row: Leaderboard + Unique Picks side by side, same height ── */}
        <HStack align="stretch" spacing={6} flexWrap="wrap" mb={6}>

          {/* Leaderboard */}
          <Box
            flex="3"
            minW={{ base: "100%", md: "400px" }}
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={6}
            h={{ base: "auto", md: "470px" }}
            display="flex"
            flexDirection="column"
          >
            <Heading size="md" color={TOP_BAR_BLUE} mb={1} flexShrink={0}>{poolName ? `${poolName} Leaderboard` : "Leaderboard"}</Heading>
            <Text fontSize="xs" color="gray.500" mb={2} flexShrink={0}>
              Click team names to view rosters. "+Yday" column shows home runs gained since yesterday.
            </Text>
            <Box overflowY="auto" flex="1">
              <Table size="sm" variant="simple">
                <Thead position="sticky" top={0} bg="white" zIndex={1}>
                  <Tr>
                    <Th w="36px">#</Th>
                    <Th>Team Name</Th>
                    <Th isNumeric w="48px">HRs</Th>
                    <Th>HR Leader</Th>
                    <Th isNumeric w="58px">+Yday</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {leaderboard.length > 0 ? (
                    leaderboard.map((entry) => {
                      const leader = hrLeader(entry);
                      const delta  = sinceYesterday(entry);
                      const isMe   = isMyEntry(entry);
                      return (
                        <Tr key={entry.roster_id} bg={isMe ? "yellow.100" : "transparent"}>
                          <Td fontWeight={isMe ? "bold" : "normal"}>
                            <WinnerRank rank={entry.rank} finished={poolFinished} />
                          </Td>
                          <Td fontWeight={isMe ? "bold" : "normal"} py={1}>
                            <Button
                              variant="link"
                              color={TOP_BAR_BLUE}
                              fontWeight={isMe ? "bold" : "medium"}
                              fontSize="sm"
                              textAlign="left"
                              whiteSpace="normal"
                              h="auto"
                              minH={0}
                              onClick={() => openTeamModal(entry)}
                            >
                              {entry.team_name}
                            </Button>
                            {entry.swap_used && (
                              <Badge ml={1} colorScheme="purple" fontSize="2xs" verticalAlign="middle">
                                SWAP
                              </Badge>
                            )}
                          </Td>
                          <Td isNumeric fontWeight={isMe ? "bold" : "normal"}>{entry.total_hrs}</Td>
                          <Td fontWeight={isMe ? "bold" : "normal"} fontSize="sm">
                            {leader ? `${leader.name} (${leader.curr_hrs})` : "—"}
                          </Td>
                          <Td isNumeric fontWeight="bold" color={delta.color}>{delta.text}</Td>
                        </Tr>
                      );
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={5}><Text color="gray.500">No leaderboard data yet.</Text></Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          </Box>

          {/* Unique Picks */}
          <Box
            flex="2"
            minW={{ base: "100%", md: "220px" }}
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={6}
            h={{ base: "auto", md: "470px" }}
            display="flex"
            flexDirection="column"
          >
            <Heading size="md" color={TOP_BAR_BLUE} mb={1} flexShrink={0}>Unique Picks - Players on One Roster</Heading>
            <Text fontSize="xs" color="gray.500" mb={2} flexShrink={0}>
              User's are highlighted
            </Text>
            <Box overflowY="auto" flex="1">
              <Table size="sm" variant="simple">
                <Thead position="sticky" top={0} bg="white" zIndex={1}>
                  <Tr>
                    <Th w="32px">#</Th>
                    <Th>Player</Th>
                    <Th isNumeric w="50px">HRs</Th>
                    <Th>Team</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {uniquePicks.length > 0 ? (
                    uniquePicks.map((p, i, arr) => {
                      const isMine = myPlayerIds.has(p.player_id);
                      const rank = i === 0 || p.curr_hrs < arr[i - 1].curr_hrs ? i + 1 : arr.findIndex((x) => x.curr_hrs === p.curr_hrs) + 1;
                      return (
                        <Tr key={p.player_id} bg={isMine ? "yellow.100" : "transparent"}>
                          <Td fontWeight={isMine ? "bold" : "normal"}>{rank}</Td>
                          <Td py={1}>
                            <HStack spacing={2} align="center">
                              <Image
                                src={playerHeadshotUrl(p.mlb_id)}
                                boxSize="32px"
                                borderRadius="full"
                                objectFit="cover"
                                bg="gray.100"
                                flexShrink={0}
                              />
                              <Text fontSize="sm" fontWeight={isMine ? "bold" : "normal"}>{p.name}</Text>
                            </HStack>
                          </Td>
                          <Td isNumeric fontWeight="bold">{p.curr_hrs}</Td>
                          <Td fontWeight={isMine ? "bold" : "normal"} fontSize="sm">{p.team_names[0]}</Td>
                        </Tr>
                      );
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={4}>
                        <Text color="gray.500">No unique picks — all players are on multiple teams.</Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </HStack>

        {/* ── Bottom: HR Stats expanded, full width ── */}
        <Box
          bg="white"
          color="black"
          border={`3px solid ${TOP_BAR_BLUE}`}
          borderRadius="lg"
          boxShadow="lg"
          p={6}
        >
          <Flex align="center" justify="space-between" mb={3} flexWrap="wrap" gap={2}>
            <Heading size="md" color={TOP_BAR_BLUE}>MLB Player Stats - Ranked by HRs</Heading>
            <InputGroup size="sm" w="220px">
              <Input
                placeholder="Search player..."
                value={playerSearch}
                onChange={(e) => setPlayerSearch(e.target.value)}
                borderColor="gray.300"
                _focus={{ borderColor: TOP_BAR_BLUE }}
              />
              {playerSearch && (
                <InputRightElement>
                  <Button size="xs" variant="ghost" onClick={() => setPlayerSearch("")} color="gray.500">✕</Button>
                </InputRightElement>
              )}
            </InputGroup>
          </Flex>
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th w="36px" display={{ base: "none", md: "table-cell" }}>#</Th>
                  <Th w="160px">Player</Th>
                  <Th w="52px">Team</Th>
                  <Th w="44px" display={{ base: "none", md: "table-cell" }}>Pos</Th>
                  <Th isNumeric w="52px">HRs</Th>
                  <Th isNumeric w="72px" display={{ base: "none", md: "table-cell" }}>2025 HRs</Th>
                  <Th isNumeric w="52px" display={{ base: "none", md: "table-cell" }}>G</Th>
                  <Th isNumeric w="52px" display={{ base: "none", md: "table-cell" }}>AB</Th>
                  <Th isNumeric w="64px" display={{ base: "none", md: "table-cell" }}>AVG</Th>
                  <Th isNumeric w="64px" display={{ base: "none", md: "table-cell" }}>SLG</Th>
                </Tr>
              </Thead>
              <Tbody>
                {pagedPlayersRanked.length > 0 ? (
                  pagedPlayersRanked.map((p) => (
                    <Tr key={p.player_id}>
                      <Td display={{ base: "none", md: "table-cell" }}>{p.hr_rank}</Td>
                      <Td w="160px" fontWeight="medium">{p.name}</Td>
                      <Td>
                        <HStack spacing={1} align="center">
                          {teamLogoUrl(p.team) && (
                            <Image src={teamLogoUrl(p.team)} boxSize="18px" objectFit="contain" flexShrink={0} />
                          )}
                          <Text fontWeight="semibold" fontSize="sm">{teamAbbr(p.team)}</Text>
                        </HStack>
                      </Td>
                      <Td display={{ base: "none", md: "table-cell" }} fontSize="xs" color="gray.500">{p.position || "—"}</Td>
                      <Td isNumeric fontWeight="bold">{p.curr_hrs}</Td>
                      <Td isNumeric display={{ base: "none", md: "table-cell" }}>{p.prev_hrs}</Td>
                      <Td isNumeric display={{ base: "none", md: "table-cell" }}>{p.games}</Td>
                      <Td isNumeric display={{ base: "none", md: "table-cell" }}>{p.at_bats}</Td>
                      <Td isNumeric display={{ base: "none", md: "table-cell" }}>{fmtStat(p.batting_avg)}</Td>
                      <Td isNumeric display={{ base: "none", md: "table-cell" }}>{fmtStat(p.slugging_pct)}</Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={10}><Text color="gray.500">No player data yet.</Text></Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
          {statsTotalPages > 1 && (
            <HStack justify="space-between" mt={3}>
              <Text fontSize="xs" color="gray.500">
                {filteredPlayersRanked.length} player{filteredPlayersRanked.length !== 1 ? "s" : ""}
              </Text>
              <HStack spacing={1}>
                <Button size="xs" variant="ghost" isDisabled={playerStatsPage === 1} onClick={() => setPlayerStatsPage(1)}>«</Button>
                <Button size="xs" variant="ghost" isDisabled={playerStatsPage === 1} onClick={() => setPlayerStatsPage((p) => p - 1)}>‹</Button>
                <Text fontSize="xs" color="gray.600" px={1}>{playerStatsPage} / {statsTotalPages}</Text>
                <Button size="xs" variant="ghost" isDisabled={playerStatsPage === statsTotalPages} onClick={() => setPlayerStatsPage((p) => p + 1)}>›</Button>
                <Button size="xs" variant="ghost" isDisabled={playerStatsPage === statsTotalPages} onClick={() => setPlayerStatsPage(statsTotalPages)}>»</Button>
              </HStack>
            </HStack>
          )}
        </Box>

      </Container>

      {/* ── Team Roster Modal ── */}
      {selectedTeam && (
        <Modal isOpen={isTeamOpen} onClose={onTeamClose} isCentered size="md" motionPreset="scale">
          <ModalOverlay bg="blackAlpha.700" />
          <ModalContent bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg">
            <ModalHeader color={TOP_BAR_BLUE} pr={10}>{selectedTeam.team_name}</ModalHeader>
            <ModalCloseButton color={TOP_BAR_BLUE} />
            <ModalBody pb={6}>
              <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Player</Th>
                    <Th isNumeric>2025 HRs</Th>
                    <Th isNumeric>2026 HRs</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(selectedTeam.players || []).map((p) => (
                    <Tr key={p.player_id} opacity={p.is_active ? 1 : 0.45}>
                      <Td py={2}>
                        <HStack spacing={2} align="center">
                          <Image
                            src={playerHeadshotUrl(p.mlb_id)}
                            boxSize="36px"
                            borderRadius="full"
                            objectFit="cover"
                            bg="gray.100"
                            flexShrink={0}
                          />
                          <Box>
                            <Text fontSize="sm" fontWeight="medium">{p.name}</Text>
                            <HStack spacing={1} mt={0.5}>
                              {!p.is_active && <Badge colorScheme="gray" fontSize="2xs">OUT</Badge>}
                              {p.is_swap_addition && <Badge colorScheme="green" fontSize="2xs">IN</Badge>}
                            </HStack>
                          </Box>
                        </HStack>
                      </Td>
                      <Td isNumeric>{p.prev_hrs}</Td>
                      <Td isNumeric fontWeight="bold">{p.curr_hrs}</Td>
                    </Tr>
                  ))}
                </Tbody>
                <Tfoot>
                  <Tr bg="gray.50">
                    <Td fontWeight="bold">Total</Td>
                    <Td isNumeric fontWeight="bold">
                      {/* Active players only, matching the current-season total,
                          which score_roster() computes with is_active = true.
                          A swapped-out player's prior-year HRs shouldn't count
                          toward a roster they're no longer on. */}
                      {(selectedTeam.players || [])
                        .filter((p) => p.is_active)
                        .reduce((s, p) => s + (p.prev_hrs || 0), 0)}
                    </Td>
                    <Td isNumeric fontWeight="bold" color={TOP_BAR_BLUE}>
                      {selectedTeam.total_hrs}
                    </Td>
                  </Tr>
                </Tfoot>
              </Table>
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}

      <RulesPayoutsPanel
        rules={MLB_RULES}
        payouts={poolPayouts}
        totalPot={poolDetail?.total_pot}
      />
    </Box>
  );
}
